// "Who said this?" Owner: WP-6 (BUILD 1).
//
// The row popover and the person/voice picker it shares with the speaker
// panel and the split flow. A row change always names its scope: after the
// target is picked, two explicit buttons follow, "Only this row" and
// "Everyone labelled X (N rows)", with no default. Picking a person who
// already names another voice also offers to merge the two voices.
// BUILD 2's voice pass may pass a `hint` for an uncertain row: the voice it
// sounds like and "Only this row" are highlighted (`data-voice-hint`), never
// preselected, so the no-default rule stands.
// Every write announces itself, so the speaker panel (which owns the
// canvas's `onChanged`) reloads the meeting. Copy: ns/meetings-speakers.ts.
import { useEffect, useState } from 'react';
import meetingsSpeakersCopy from '@/i18n/ns/meetings-speakers';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { workspaceApi } from './api';
import { useWorkspace } from './context';
import { meetingsApi } from './meetings-api';
import {
  labelRowCount,
  mergeMovedRows,
  panelVoices,
  rootVoice,
  speakerIndex,
  voiceOfPerson,
  voiceSpeaker,
  type RowSpeaker,
} from './meeting-speaker-model';
import type {
  CompanyMeeting,
  CompanyMember,
  MeetingRowSpeakerInput,
  MeetingSegment,
  MeetingSpeaker,
} from './types';

export type SpeakersCopy = Readonly<Record<keyof (typeof meetingsSpeakersCopy)['en'], string>>;
export const useSpeakersCopy = () => useNamespaceCopy(meetingsSpeakersCopy);

// ── Change announcements (the transcript has no onChanged; the panel does) ──

const SPEAKERS_CHANGED = 'poneva:meeting-speakers-changed';

export function announceSpeakerChange(meetingId: string) {
  window.dispatchEvent(new CustomEvent(SPEAKERS_CHANGED, { detail: meetingId }));
}

export function useSpeakerChanges(meetingId: string, listener: () => void) {
  useEffect(() => {
    const handle = (event: Event) => {
      if ((event as CustomEvent<string>).detail === meetingId) listener();
    };
    window.addEventListener(SPEAKERS_CHANGED, handle);
    return () => window.removeEventListener(SPEAKERS_CHANGED, handle);
  }, [meetingId, listener]);
}

// ── Names ───────────────────────────────────────────────────────────────────

/** A speaker as the UI names it: the person, else "Speaker N", else unknown. */
export function speakerName(speaker: RowSpeaker, copy: SpeakersCopy) {
  if (speaker.name) return speaker.name;
  if (speaker.speakerNumber != null) return formatCopy(copy.speakerN, { n: speaker.speakerNumber });
  return copy.unlabelled;
}

export const voiceName = (meeting: CompanyMeeting, voice: MeetingSpeaker, copy: SpeakersCopy) =>
  speakerName(voiceSpeaker(voice, speakerIndex(meeting)), copy);

export function useCompanyMembers(active: boolean) {
  const [members, setMembers] = useState<CompanyMember[] | null>(null);
  useEffect(() => {
    if (!active || members) return;
    let live = true;
    void workspaceApi
      .members()
      .then((list) => live && setMembers(list))
      .catch(() => live && setMembers([]));
    return () => {
      live = false;
    };
  }, [active, members]);
  return members ?? [];
}

export function failureCopy(failure: unknown, copy: SpeakersCopy) {
  const code = (failure as { code?: string } | null)?.code;
  return code === 'meeting_split_needs_words' ? copy.splitNeedsWords : copy.changeFailed;
}

// ── The picker ──────────────────────────────────────────────────────────────

export type SpeakerTarget =
  | { kind: 'participant'; participantId: string; name: string }
  | { kind: 'member'; userId: string; name: string }
  | { kind: 'guest'; name: string }
  | { kind: 'voice'; label: string; name: string }
  | { kind: 'not_participant' }
  | { kind: 'same' };

export const targetName = (target: SpeakerTarget, copy: SpeakersCopy) =>
  'name' in target ? target.name : target.kind === 'same' ? copy.sameSpeaker : copy.notParticipant;

/** The attendee id a person target writes; a member or guest is added to the meeting first. */
export async function participantFor(meetingId: string, target: SpeakerTarget) {
  if (target.kind === 'participant') return target.participantId;
  if (target.kind === 'member')
    return (await meetingsApi.addParticipant(meetingId, { kind: 'member', userId: target.userId }))
      .id;
  if (target.kind === 'guest')
    return (await meetingsApi.addParticipant(meetingId, { kind: 'guest', name: target.name })).id;
  return null;
}

type PickerOptions = {
  /**
   * Offer other voices (merge or move), leaving this one out, under `title`.
   * `hint` is a voice to highlight, never to preselect.
   */
  voices?: { except: string | null; title?: string; hint?: string | null };
  notParticipant?: boolean;
  same?: boolean;
  /** Attendee ids to list first, marked as suggested. */
  suggested?: string[];
};

function GuestInput({ copy, onAdd }: { copy: SpeakersCopy; onAdd: (name: string) => void }) {
  const [name, setName] = useState('');
  const add = () => {
    if (name.trim()) onAdd(name.trim());
  };
  return (
    <div className="ws-speaker-guest">
      <input
        type="text"
        value={name}
        placeholder={copy.guestName}
        aria-label={copy.guestName}
        maxLength={200}
        onChange={(event) => setName(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') add();
        }}
      />
      <button className="ws-button" disabled={!name.trim()} onClick={add}>
        {copy.addGuestButton}
      </button>
    </div>
  );
}

function OptionList({
  title,
  options,
}: {
  title: string;
  options: { key: string; label: string; hint?: boolean; onPick: () => void }[];
}) {
  if (!options.length) return null;
  return (
    <section>
      <h4>{title}</h4>
      <div className="ws-speaker-options">
        {options.map((option) => (
          <button
            key={option.key}
            className="ws-speaker-option"
            data-voice-hint={option.hint || undefined}
            onClick={option.onPick}
          >
            {option.label}
          </button>
        ))}
      </div>
    </section>
  );
}

function attendeeOptions(
  meeting: CompanyMeeting,
  suggested: string[],
  copy: SpeakersCopy,
  onPick: (target: SpeakerTarget) => void,
) {
  const people = [...(meeting.participants ?? [])].sort(
    (left, right) => Number(suggested.includes(right.id)) - Number(suggested.includes(left.id)),
  );
  return people.map((person) => ({
    key: person.id,
    label: suggested.includes(person.id)
      ? formatCopy(copy.suggested, { name: person.displayName })
      : person.displayName,
    onPick: () =>
      onPick({ kind: 'participant', participantId: person.id, name: person.displayName }),
  }));
}

function memberOptions(
  meeting: CompanyMeeting,
  members: CompanyMember[],
  onPick: (target: SpeakerTarget) => void,
) {
  const present = new Set((meeting.participants ?? []).map((person) => person.userId));
  return members
    .filter((member) => !present.has(member.id))
    .map((member) => ({
      key: member.id,
      label: member.displayName,
      onPick: () => onPick({ kind: 'member', userId: member.id, name: member.displayName }),
    }));
}

function voiceOptions(
  meeting: CompanyMeeting,
  voices: NonNullable<PickerOptions['voices']>,
  copy: SpeakersCopy,
  onPick: (target: SpeakerTarget) => void,
) {
  return panelVoices(meeting)
    .filter((voice) => voice.label !== voices.except)
    .map((voice) => {
      const name = voiceName(meeting, voice, copy);
      return {
        key: voice.label,
        label: name,
        hint: voice.label === voices.hint,
        onPick: () => onPick({ kind: 'voice', label: voice.label, name }),
      };
    });
}

export function SpeakerTargetPicker({
  meeting,
  members,
  copy,
  options = {},
  onPick,
}: {
  meeting: CompanyMeeting;
  members: CompanyMember[];
  copy: SpeakersCopy;
  options?: PickerOptions;
  onPick: (target: SpeakerTarget) => void;
}) {
  return (
    <div className="ws-speaker-picker">
      {options.same && (
        <button className="ws-speaker-option" onClick={() => onPick({ kind: 'same' })}>
          {copy.sameSpeaker}
        </button>
      )}
      <OptionList
        title={copy.attendees}
        options={attendeeOptions(meeting, options.suggested ?? [], copy, onPick)}
      />
      <OptionList title={copy.companyMembers} options={memberOptions(meeting, members, onPick)} />
      <section>
        <h4>{copy.addGuest}</h4>
        <GuestInput copy={copy} onAdd={(name) => onPick({ kind: 'guest', name })} />
      </section>
      {options.voices && (
        <OptionList
          title={options.voices.title ?? copy.otherVoices}
          options={voiceOptions(meeting, options.voices, copy, onPick)}
        />
      )}
      {options.notParticipant && (
        <button className="ws-speaker-option" onClick={() => onPick({ kind: 'not_participant' })}>
          {copy.notParticipant}
        </button>
      )}
    </div>
  );
}

// ── The row popover ─────────────────────────────────────────────────────────

type Scope = 'row' | 'label';

async function rowInput(
  meetingId: string,
  scope: Scope,
  target: SpeakerTarget,
): Promise<MeetingRowSpeakerInput> {
  if (target.kind === 'voice') return { scope, label: target.label };
  if (target.kind === 'guest') return { scope, guest: { name: target.name } };
  const participantId = await participantFor(meetingId, target);
  return { scope, participantId: participantId as string };
}

function ScopeChoice({
  meeting,
  segment,
  target,
  copy,
  hinted,
  onChoose,
  onMerge,
}: {
  meeting: CompanyMeeting;
  segment: MeetingSegment;
  target: SpeakerTarget;
  copy: SpeakersCopy;
  /** The voice pass flagged this row: "Only this row" is highlighted, not chosen. */
  hinted: boolean;
  onChoose: (scope: Scope) => void;
  onMerge: (into: string) => void;
}) {
  const index = speakerIndex(meeting);
  const voice = rootVoice(index.voices, segment.speaker);
  const owner =
    target.kind === 'participant'
      ? voiceOfPerson(meeting, target.participantId, voice?.label ?? null)
      : null;
  const into = target.kind === 'voice' ? rootVoice(index.voices, target.label) : null;
  const rows = !voice
    ? 0
    : into
      ? mergeMovedRows(meeting, voice, into)
      : labelRowCount(meeting, voice.label, 'person', segment.index);
  return (
    <div className="ws-speaker-scope" role="group" aria-label={targetName(target, copy)}>
      <p>{targetName(target, copy)}</p>
      <button
        className="ws-button"
        data-voice-hint={hinted || undefined}
        onClick={() => onChoose('row')}
      >
        {copy.onlyThisRow}
      </button>
      {voice && (
        <button className="ws-button" onClick={() => onChoose('label')}>
          {formatCopy(copy.everyoneLabelled, {
            label: speakerName(voiceSpeaker(voice, index), copy),
            rows,
          })}
        </button>
      )}
      {voice && owner && (
        <button className="ws-button" onClick={() => onMerge(owner.label)}>
          {formatCopy(copy.mergeWithVoice, { name: targetName(target, copy) })}
        </button>
      )}
    </div>
  );
}

export function MeetingSpeakerPopover({
  meeting,
  segment,
  members,
  hint,
  onClose,
}: {
  meeting: CompanyMeeting;
  segment: MeetingSegment;
  members: CompanyMember[];
  /** The label the voice pass says this row sounds like (BUILD 2). */
  hint?: string;
  onClose: () => void;
}) {
  const ws = useWorkspace();
  const copy = useSpeakersCopy();
  const [target, setTarget] = useState<SpeakerTarget | null>(null);
  const [busy, setBusy] = useState(false);
  const voices = speakerIndex(meeting).voices;
  const voice = rootVoice(voices, segment.speaker);
  const hinted = hint ? (rootVoice(voices, hint)?.label ?? null) : null;
  async function run(write: () => Promise<unknown>) {
    setBusy(true);
    try {
      await write();
      announceSpeakerChange(meeting.id);
      onClose();
    } catch (failure) {
      ws.notify(failureCopy(failure, copy));
    } finally {
      setBusy(false);
    }
  }
  const choose = (scope: Scope) =>
    run(async () =>
      meetingsApi.setSegmentSpeaker(
        meeting.id,
        segment.index,
        await rowInput(meeting.id, scope, target as SpeakerTarget),
      ),
    );
  const merge = (into: string) =>
    run(() => meetingsApi.mergeSpeaker(meeting.id, (voice as MeetingSpeaker).label, into));
  return (
    <div
      className="ws-speaker-popover"
      role="dialog"
      aria-label={copy.whoSaidThis}
      aria-busy={busy}
    >
      <header>
        <strong>{copy.whoSaidThis}</strong>
        <button className="ws-citation" onClick={onClose}>
          {copy.cancel}
        </button>
      </header>
      {target ? (
        <ScopeChoice
          meeting={meeting}
          segment={segment}
          target={target}
          copy={copy}
          hinted={hinted !== null}
          onChoose={(scope) => void choose(scope)}
          onMerge={(into) => void merge(into)}
        />
      ) : (
        <SpeakerTargetPicker
          meeting={meeting}
          members={members}
          copy={copy}
          options={{ voices: { except: voice?.label ?? null, hint: hinted } }}
          onPick={setTarget}
        />
      )}
    </div>
  );
}
